<script setup lang="ts">
import type { SelectableTab } from '@/types/tabs';

const props = withDefaults(
  defineProps<{
    tabs: readonly SelectableTab[];
    modelValue: string[];
    minSelected?: number;
  }>(),
  {
    minSelected: 1,
  },
);

const emit = defineEmits<{
  'update:modelValue': [values: string[]];
}>();

function selectedValues(): string[] {
  return props.modelValue?.length ? [...props.modelValue] : [];
}

function isSelected(value: string) {
  return selectedValues().includes(value);
}

function onSingleClick(value: string) {
  const current = selectedValues();
  const idx = current.indexOf(value);
  if (idx >= 0) {
    if (current.length > props.minSelected) {
      current.splice(idx, 1);
      emit('update:modelValue', current);
    }
  } else {
    emit('update:modelValue', [...current, value]);
  }
}

function onDoubleClick(value: string) {
  emit('update:modelValue', [value]);
}

function handleClick(value: string, event: MouseEvent) {
  if (event.detail === 2) {
    onDoubleClick(value);
  } else {
    onSingleClick(value);
  }
}
</script>

<template>
  <div class="flex gap-1 bg-[#f1f5f9] p-1">
    <button
      v-for="tab in tabs"
      :key="tab.value"
      type="button"
      class="min-w-0 flex-1 cursor-pointer rounded-md border-none px-3 py-2 text-center text-sm font-medium transition focus:outline-none"
      :class="
        isSelected(tab.value)
          ? 'bg-white text-gray-800 shadow-sm'
          : 'bg-transparent text-slate-500 hover:text-slate-700'
      "
      :aria-pressed="isSelected(tab.value)"
      @click="handleClick(tab.value, $event)"
    >
      {{ tab.label }}
    </button>
  </div>
</template>
